"use client";

import type React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEVIS_STATUS } from "modules/devis/types/devis.interface";
import { INVOICE_STATUS } from "modules/invoice/types/invoice.interface";
interface StatusFilterProps {
  selectedStatus: string | null;
  onChange: (status: string | null) => void;
  type: "devis" | "invoice";
}

const StatusFilter: React.FC<StatusFilterProps> = ({
  selectedStatus,
  onChange,
  type,
}) => {
  const statuses = [
    { value: DEVIS_STATUS.DRAFT, label: "En cours" },
    { value: DEVIS_STATUS.SENT, label: "Envoyé" },
    { value: DEVIS_STATUS.ACCEPTED, label: "Accepté" },
    { value: DEVIS_STATUS.REJECTED, label: "Rejeté" },
  ];

  const status_invoices = [
    { value: INVOICE_STATUS.DRAFT, label: "En cours" },
    { value: INVOICE_STATUS.SENT, label: "Envoyé" },
    { value: INVOICE_STATUS.PAID, label: "Payé" },
  ];

  return (
    <div className="relative z-50">
      <Select
        value={selectedStatus || "all"}
        onValueChange={(value) => onChange(value === "all" ? null : value)}
      >
        <SelectTrigger className="w-full border-none bg-lightPrimary dark:bg-navy-900">
          <SelectValue placeholder="Sélectionner un statut" />
        </SelectTrigger>
        <SelectContent className="relative z-50 bg-white">
          <SelectItem value="all">Tous les statuts</SelectItem>
          {type === "devis" &&
            statuses.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <div className="flex items-center">
                  <StatusBadge status={status.value} />
                  <span className="ml-2">{status.label}</span>
                </div>
              </SelectItem>
            ))}
          {type === "invoice" &&
            status_invoices.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <div className="flex items-center">
                  <StatusBadge status={status.value} />
                  <span className="ml-2">{status.label}</span>
                </div>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Status badge component with appropriate colors
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusColor = () => {
    switch (status) {
      case DEVIS_STATUS.DRAFT:
        return "bg-gray-200 dark:bg-gray-700";
      case DEVIS_STATUS.SENT:
        return "bg-blue-200 dark:bg-blue-900";
      case DEVIS_STATUS.REJECTED:
        return "bg-red-200 dark:bg-red-900";
      case DEVIS_STATUS.ACCEPTED:
        return "bg-green-200 dark:bg-green-900";
      case INVOICE_STATUS.PAID:
        return "bg-green-200 dark:bg-green-900";
      default:
        return "bg-gray-200 dark:bg-gray-700";
    }
  };

  const getStatusEmoji = () => {
    switch (status) {
      case DEVIS_STATUS.DRAFT:
        return "📝";
      case DEVIS_STATUS.SENT:
        return "📨";
      case DEVIS_STATUS.ACCEPTED:
        return "👍";
      case DEVIS_STATUS.REJECTED:
        return "🚫";
      case INVOICE_STATUS.PAID:
        return "💰";
      case INVOICE_STATUS.DRAFT:
        return "📝";
      case INVOICE_STATUS.SENT:
        return "📨";
      default:
        return "";
    }
  };

  return (
    <span className="flex items-center gap-1">
      <span>{getStatusEmoji()}</span>
      <span
        className={`inline-block h-3 w-3 rounded-full ${getStatusColor()}`}
      />
    </span>
  );
};

export default StatusFilter;
